import {
  CallWindowLimitDateBulkRequest,
  type CallWindowLimitDateEntry,
  type CallWindowLimitDateFilter,
  type CallWindowLimitDateList,
} from '@kalos/kalos-rpc';
import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { endOfWeek, format, parseISO, startOfWeek } from 'date-fns';
import { useMemo } from 'react';

import { windowDateKey, type WindowLimitOverride } from '../../tools/callWindowLimits';
import { CallWindowLimitDateClientService } from '../../tools/helpers';
import { queryKeys } from './constants';

export const useCallWindowLimitDateRangeQuery = ({
  filter,
  enabled = true,
  options,
}: {
  filter: CallWindowLimitDateFilter;
  enabled?: boolean;
  options?: Partial<UseQueryOptions<CallWindowLimitDateList, Error>>;
}) => {
  return useQuery({
    queryFn: async () => {
      return await CallWindowLimitDateClientService.loadCallWindowLimitDates({ filter });
    },
    queryKey: [
      queryKeys.callWindowLimitDate.root,
      queryKeys.callWindowLimitDate.list,
      queryKeys.callWindowLimitDate.getHash(filter),
    ],
    enabled: enabled && !!filter.dateStart && !!filter.dateEnd,
    ...options,
  });
};

/**
 * Active date overrides inside a range, keyed `${callWindowId}:${yyyy-MM-dd}` for reading a
 * window's effective limits on a given date via resolveWindowLimits.
 *
 * Not filtered by department for the same reason as useWindowLimitOverrides: an override only
 * ever exists for a department-owned window, so the call_window_id already identifies the
 * department. Callers sharing a range share one fetch (same query key).
 */
export const useWindowLimitDateOverrides = ({
  dateStart,
  dateEnd,
  departmentId,
  enabled = true,
}: {
  dateStart: string;
  dateEnd: string;
  departmentId?: number;
  enabled?: boolean;
}) => {
  const { data } = useCallWindowLimitDateRangeQuery({
    filter: { dateStart, dateEnd, departmentId },
    enabled,
  });

  return useMemo(() => {
    const map: Record<string, WindowLimitOverride> = {};
    (data?.results ?? []).forEach((l) => {
      map[windowDateKey(l.callWindowId, l.overrideDate)] = {
        windowCount: l.windowCount,
        maintMax: l.maintMax,
      };
    });
    return map;
  }, [data]);
};

/**
 * Date overrides for the week containing `date`, for the callers that resolve limits on one
 * date at a time (a scheduler card, a job-creation step).
 *
 * The range is widened to the whole week, and on the same Monday boundary the scheduler's week
 * view uses, so every card in a week resolves from one shared fetch rather than one request
 * per day. A day view lands on the same key as the week that contains it.
 */
export const useWindowLimitDateOverridesForDate = ({
  date,
  departmentId,
  enabled = true,
}: {
  date: string;
  departmentId?: number;
  enabled?: boolean;
}) => {
  const { dateStart, dateEnd } = useMemo(() => {
    if (!date) return { dateStart: '', dateEnd: '' };
    const parsed = parseISO(date.slice(0, 10));
    if (isNaN(parsed.getTime())) return { dateStart: '', dateEnd: '' };
    return {
      dateStart: format(startOfWeek(parsed, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
      dateEnd: format(endOfWeek(parsed, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
    };
  }, [date]);

  return useWindowLimitDateOverrides({
    dateStart,
    dateEnd,
    departmentId,
    enabled: enabled && !!dateStart,
  });
};

// A whole multi-date selection is written in one call: the backend applies the entries in a
// single transaction, so a save can't leave half the selected dates overridden.
export const useCallWindowLimitDateBulkUpsertMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (entries: CallWindowLimitDateEntry[]) => {
      if (entries.length === 0) return undefined;
      return await CallWindowLimitDateClientService.BulkUpsert(
        CallWindowLimitDateBulkRequest.create({ entries }),
      );
    },
    // Awaited so mutateAsync resolves only once the list has been refetched: otherwise isPending
    // flips false while the stale rows are still on screen, matching useWindowBlockQuery.
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [queryKeys.callWindowLimitDate.root] });
    },
  });
};

// Clearing is the same shape: a row set back to its weekly value, or "Reset these dates to
// weekly" across the whole selection. Only call_window_id and override_date are read.
export const useCallWindowLimitDateBulkDeleteMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (entries: CallWindowLimitDateEntry[]) => {
      if (entries.length === 0) return undefined;
      return await CallWindowLimitDateClientService.BulkDelete(
        CallWindowLimitDateBulkRequest.create({ entries }),
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [queryKeys.callWindowLimitDate.root] });
    },
  });
};
